import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NumbersOutlinedIcon from '@mui/icons-material/NumbersOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

import { Header } from 'components/Header';
import { useServiceConfigs } from '../context/ServiceConfigContext';
import { getServiceConfigs } from '../services/api';
import { formatApiErrorMessage, logApiError } from '../services/errors';
import { ServiceConfig } from '../services/types';
import { DEFAULT_SERVICE_ICON, getServiceIconAsset } from '../utils/serviceIcons';
import { applyCs1ProductCareOverrides, sortServiceConfigs } from '../utils/serviceConfig';
import { isGoldFromSelection } from '../utils/metal';

interface CustomerOwnedServiceOptionsProps {
  externalIdentity: string;
}

type NormalizedLocation = {
  id: string;
  label: string;
  icon: React.ReactNode;
  colorKey: 'info' | 'primary';
};

const normalizeLocations = (
  locations: string[] = [],
  { isQuick = false, forceProductCare = false }: { isQuick?: boolean; forceProductCare?: boolean } = {},
): NormalizedLocation[] => {
  if (!locations?.length) return [];
  const expanded = locations
    .flatMap((loc) => String(loc).split(',').map((value) => value.trim()))
    .filter(Boolean);

  const filtered = expanded.filter((loc) => {
    const isProductCare = /Kerrville/i.test(loc) || /Product\s*Care/i.test(loc);
    if (forceProductCare) {
      return isProductCare;
    }
    if (isQuick) {
      return /Store/i.test(loc);
    }
    return true;
  });

  const unique = Array.from(new Set(filtered));
  return unique.map((loc) => {
    const normalized = loc
      .replace(/Kerrville/g, 'Product Care')
      .replace(/Store/g, 'In Store');
    const isProductCare = /Product Care/i.test(normalized);
    return {
      id: loc,
      label: normalized,
      icon: isProductCare
        ? <LocalShippingOutlinedIcon fontSize="small" />
        : <StorefrontOutlinedIcon fontSize="small" />,
      colorKey: isProductCare ? 'info' : 'primary',
    };
  });
};

const ServiceTile: React.FC<{
  config: ServiceConfig;
  onSelect: () => void;
  isQuick: boolean;
  forceProductCare: boolean;
}> = ({ config, onSelect, isQuick, forceProductCare }) => {
  const locations = normalizeLocations(config.locationServiced, { isQuick, forceProductCare });
  const iconSrc = useMemo(() => getServiceIconAsset(config.icon), [config.icon]);
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 2,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.4),
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
        },
      })}
      onClick={onSelect}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={(theme) => ({
              width: 48,
              height: 48,
              borderRadius: '0 0 16px 0',
              bgcolor: theme.palette.grey[50],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: `0.8px solid ${alpha(theme.palette.divider, 0.6)}`,
              overflow: 'hidden',
            })}
          >
            <Box
              component="img"
              src={iconSrc}
              alt={`${config.description} icon`}
              sx={{ width: 28, height: 28 }}
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = DEFAULT_SERVICE_ICON;
              }}
            />
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {config.description}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`$${config.cost.toFixed(2)}`} color="primary" variant="outlined" />
          {config.allowQuantity && (
            <Chip
              size="small"
              icon={<NumbersOutlinedIcon sx={{ fontSize: 16 }} />}
              label="Quantity available"
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
          )}
        </Stack>
        {!!locations.length && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {locations.map((loc) => (
              <Chip
                key={loc.id}
                size="small"
                label={loc.label}
                sx={(theme) => ({
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette[loc.colorKey].main, 0.12),
                  color: theme.palette[loc.colorKey].main,
                  '& .MuiChip-icon': {
                    color: theme.palette[loc.colorKey].main,
                  },
                })}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const CustomerOwnedServiceOptionsPage: React.FC<CustomerOwnedServiceOptionsProps> = ({ externalIdentity }) => {
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: { metalType?: string; productType?: string; measuredSize?: string; condition?: string };
  };
  const { state } = location;

  const {
    configs,
    setConfigs,
    selectedServices,
    serviceType,
    storeId,
    productId,
  } = useServiceConfigs();

  const metalType = state?.metalType ?? 'Non-Gold';
  const productType = state?.productType;
  const measuredSize = state?.measuredSize;
  const condition = state?.condition;

  const [visibleConfigs, setVisibleConfigs] = useState<ServiceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');

  const isQuick = serviceType === 'QUICK';
  const isCs1Store = storeId?.toUpperCase?.() === 'CS1';
  const isGold = useMemo(() => isGoldFromSelection(metalType, productId), [metalType, productId]);

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        if (!externalIdentity) {
          setError('Missing authentication context.');
          return;
        }
        if (!configs) {
          const allConfigs = await getServiceConfigs(externalIdentity);
          const ordered = sortServiceConfigs(
            applyCs1ProductCareOverrides(allConfigs, storeId)
          );
          setConfigs(ordered);
          setVisibleConfigs(ordered);
        } else {
          setVisibleConfigs(
            sortServiceConfigs(applyCs1ProductCareOverrides(configs ?? [], storeId))
          );
        }
      } catch (err) {
        logApiError('Loading customer-owned services', err);
        setError(formatApiErrorMessage(err, 'Failed to load services.'));
      } finally {
        setLoading(false);
      }
    };
    fetchConfigs();
  }, [configs, externalIdentity, setConfigs, storeId]);

  useEffect(() => {
    if (!configs) return;
    const filtered = configs.filter((cfg) => {
      const metalMatches = cfg.metal === 'Both' || cfg.metal === (isGold ? 'Gold' : 'Non-Gold');
      if (!metalMatches) return false;

      const locations = (cfg.locationServiced ?? [])
        .flatMap((loc) => String(loc).split(',').map((value) => value.trim()))
        .filter(Boolean);

      if (isCs1Store) {
        return locations.some((value) => /Kerrville/i.test(value) || /Product\s*Care/i.test(value));
      }

      if (isQuick) {
        return locations.includes('Store');
      }
      return locations.length > 0;
    });
    setVisibleConfigs(sortServiceConfigs(filtered));
  }, [configs, isGold, isQuick, isCs1Store]);

  const filteredConfigs = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    if (!query) return visibleConfigs;
    return visibleConfigs.filter((cfg) => cfg.description.toLowerCase().includes(query));
  }, [searchInput, visibleConfigs]);

  const handleClearSearch = () => {
    setSearchInput('');
  };

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} display="flex" flexDirection="column" alignItems="center" gap={2}>
        <Alert severity="warning" variant="outlined" sx={{ width: '100%', maxWidth: 480 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!metalType || !productType) {
    return (
      <Box p={4} display="flex" flexDirection="column" alignItems="center" gap={2}>
        <Alert severity="info" variant="outlined" sx={{ width: '100%', maxWidth: 520 }}>
          <AlertTitle>Missing selection</AlertTitle>
          We need the metal and product type to suggest services. Please go back and choose them.
        </Alert>
      </Box>
    );
  }

  const noServicesMessage = isQuick && isCs1Store
    ? 'Quick services are not available for this location.'
    : 'No services available for the selected criteria.';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={<Typography variant="h6">Guest Owned Services</Typography>}
        subtitle={isQuick ? 'In-store eligible services' : 'All service options'}
        align="left"
        sticky
        showBackButton
        onBack={() => navigate(-1)}
        elevateOnScroll
        serviceCount={selectedServices.length}
        onBadgeClick={() =>
          navigate('/summary', {
            state: { metalType, productType, measuredSize, condition },
          })
        }
      />

      <Card
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        })}
      >
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Metal: ${metalType}`} variant="outlined" />
            <Chip label={`Product: ${productType}`} variant="outlined" />
            <Chip label={`Measured Size: ${measuredSize ?? '—'}`} variant="outlined" />
            <Chip label={`Condition: ${condition ?? '—'}`} variant="outlined" />
            <Chip
              icon={isQuick ? <BoltOutlinedIcon /> : <ScheduleOutlinedIcon />}
              label={isQuick ? 'Quick Service' : 'Standard Service'}
              variant="outlined"
              color={isQuick ? 'primary' : 'default'}
            />
          </Stack>
        </CardContent>
      </Card>

      <Paper
        elevation={0}
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          mb: 4,
          p: 2,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          background: alpha(theme.palette.primary.light, 0.05),
          backdropFilter: 'saturate(120%) blur(4px)',
        })}
      >
        <Typography variant="h6" gutterBottom>
          Available services
        </Typography>

        <Box mb={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by service description"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ opacity: 0.7 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchInput ? (
                    <IconButton size="small" onClick={handleClearSearch} aria-label="Clear search">
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {filteredConfigs.length ? (
          <Grid container spacing={2}>
            {filteredConfigs.map((config) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={config.id}>
                <ServiceTile
                  config={config}
                  isQuick={isQuick}
                  forceProductCare={isCs1Store}
                  onSelect={() =>
                    navigate(`/services/details/${config.id}`, {
                      state: { metalType, productType, measuredSize, condition },
                    })
                  }
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box py={4} textAlign="center">
            <Typography variant="subtitle2" gutterBottom>
              {visibleConfigs.length ? 'No services match your search.' : noServicesMessage}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please adjust the selection or try a different service type.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CustomerOwnedServiceOptionsPage;
